/**
 * Expandable panel showing context window usage details.
 *
 * Gated behind the `devMode` feature flag. Shows token counts,
 * context budget utilization, and compaction pipeline activity
 * (pruning, summarization, trimming).
 */

import { ChevronDown, ChevronRight, Cpu } from "lucide-react";
import { useState } from "react";
import type { ContextInfo } from "@/hooks/useChatStreaming";

interface ContextDetailPanelProps {
  info: ContextInfo;
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function usageColor(percent: number): string {
  if (percent >= 90) return "text-red-400";
  if (percent >= 70) return "text-amber-400";
  return "text-emerald-400";
}

function barColor(percent: number): string {
  if (percent >= 90) return "bg-red-500";
  if (percent >= 70) return "bg-amber-500";
  return "bg-emerald-500";
}

export function ContextDetailPanel({ info }: Readonly<ContextDetailPanelProps>) {
  const [isExpanded, setIsExpanded] = useState(false);

  const pct = Math.min(info.percent_used, 100);
  const hadActivity = info.groups_pruned > 0 || info.groups_trimmed > 0 || info.summarized;

  return (
    <div className="mt-2 rounded-lg border border-border-secondary bg-surface-secondary text-xs font-mono">
      {/* Collapsed summary bar */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center gap-2 px-3 py-1.5 text-content-secondary hover:text-content-primary transition-colors"
      >
        {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <Cpu className="w-3 h-3" />
        <span className={usageColor(pct)}>{formatTokens(info.prompt_tokens)} tokens</span>
        <span className="text-content-tertiary">·</span>
        <span className={usageColor(pct)}>{pct}% used</span>
        {hadActivity && (
          <>
            <span className="text-content-tertiary">·</span>
            <span className="text-content-tertiary">
              {[
                info.groups_pruned > 0 ? `${info.groups_pruned} pruned` : null,
                info.groups_trimmed > 0 ? `${info.groups_trimmed} trimmed` : null,
                info.summarized ? "summarized" : null,
              ]
                .filter(Boolean)
                .join(", ")}
            </span>
          </>
        )}
        <span className="ml-auto text-content-tertiary">{info.model}</span>
      </button>

      {/* Expanded detail view */}
      {isExpanded && (
        <div className="border-t border-border-secondary px-3 py-2 space-y-2">
          {/* Progress bar */}
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-surface-tertiary rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${barColor(pct)}`} style={{ width: `${pct}%` }} />
            </div>
            <span className={`${usageColor(pct)} whitespace-nowrap`}>{pct}%</span>
          </div>

          {/* Token breakdown */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-content-secondary">
            <div className="flex justify-between">
              <span>Context window</span>
              <span className="text-content-primary">{formatTokens(info.context_window)}</span>
            </div>
            <div className="flex justify-between">
              <span>Max output</span>
              <span className="text-content-primary">{formatTokens(info.max_output_tokens)}</span>
            </div>
            <div className="flex justify-between">
              <span>Tool definitions</span>
              <span className="text-content-primary">{formatTokens(info.tool_tokens)}</span>
            </div>
            <div className="flex justify-between">
              <span>Available budget</span>
              <span className="text-content-primary">{formatTokens(info.budget)}</span>
            </div>
            <div className="flex justify-between">
              <span>Prompt tokens</span>
              <span className={usageColor(pct)}>{formatTokens(info.prompt_tokens)}</span>
            </div>
            <div className="flex justify-between">
              <span>Messages</span>
              <span className="text-content-primary">
                {info.messages_after_trim}
                {info.messages_loaded !== info.messages_after_trim && (
                  <span className="text-content-tertiary"> / {info.messages_loaded} loaded</span>
                )}
              </span>
            </div>
          </div>

          {/* Compaction pipeline activity */}
          {hadActivity && (
            <div className="border-t border-border-secondary pt-2 space-y-1">
              <span className="text-content-tertiary">Compaction pipeline</span>
              <div className="flex flex-wrap gap-2">
                {info.groups_pruned > 0 && (
                  <span className="px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400">
                    {info.groups_pruned} groups pruned
                  </span>
                )}
                {info.summarized && (
                  <span className="px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-400">summarized</span>
                )}
                {info.groups_trimmed > 0 && (
                  <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400">
                    {info.groups_trimmed} groups trimmed
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
